import React, { useState } from "react";
import { Modal, DatePicker, Button, Space, Typography, Alert, Spin } from "antd";
import dayjs from "dayjs";
import { FrontDeskBooking } from "../../../types";

const { Text } = Typography;

interface ExtendStayModalProps {
  open: boolean;
  booking: FrontDeskBooking | null;
  onCancel: () => void;
  onExtend: (bookingId: string, newCheckOutDate: string) => Promise<void>;
}

export const ExtendStayModal: React.FC<ExtendStayModalProps> = ({
  open,
  booking,
  onCancel,
  onExtend,
}) => {
  const [newCheckOutDate, setNewCheckOutDate] = useState<dayjs.Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentCheckOut = booking?.check_out_date
    ? dayjs(booking.check_out_date)
    : null;

  const nightsAdded = currentCheckOut && newCheckOutDate
    ? Math.max(0, newCheckOutDate.diff(currentCheckOut, "days"))
    : 0;

  const totalNights = booking?.check_in_date && newCheckOutDate
    ? Math.max(1, newCheckOutDate.diff(dayjs(booking.check_in_date), "days"))
    : 0;

  const handleExtend = async () => {
    if (!booking || !newCheckOutDate) return;

    setLoading(true);
    setError(null);
    try {
      await onExtend(booking.id, newCheckOutDate.format("YYYY-MM-DD"));
      setNewCheckOutDate(null);
      onCancel();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to extend stay");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setNewCheckOutDate(null);
    setError(null);
    onCancel();
  };

  return (
    <Modal
      title="Extend Stay"
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={420}
    >
      {booking && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 8 }}>
            <div><Text strong>Guest:</Text> {booking.guest_name}</div>
            <div><Text strong>Room:</Text> {booking.room_number || "Not allocated"}</div>
            <div>
              <Text strong>Current Stay:</Text>{" "}
              {dayjs(booking.check_in_date).format("MMM D")} – {currentCheckOut?.format("MMM D, YYYY")}
              {" "}({booking.check_out_date ? Math.max(1, currentCheckOut!.diff(dayjs(booking.check_in_date), "days")) : 0} nights)
            </div>
          </div>

          <div>
            <Text strong>New Check-Out Date</Text>
            <DatePicker
              value={newCheckOutDate}
              onChange={(date) => {
                setNewCheckOutDate(date);
                setError(null);
              }}
              disabledDate={(current) =>
                current && current.isBefore(dayjs(booking.check_out_date).add(1, "day"))
              }
              style={{ width: "100%", marginTop: 8 }}
              format="YYYY-MM-DD"
              placeholder="Select new check-out date"
            />
          </div>

          {nightsAdded > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${nightsAdded} additional night${nightsAdded > 1 ? "s" : ""} — Total: ${totalNights} nights`}
            />
          )}

          {error && (
            <Alert type="error" showIcon message={error} />
          )}

          <Space style={{ justifyContent: "flex-end" }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleExtend}
              disabled={!newCheckOutDate || loading}
              loading={loading}
            >
              Extend Stay
            </Button>
          </Space>
        </div>
      )}
    </Modal>
  );
};
